import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Share2, ArrowLeft, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PageHeader, AVATAR_ICONS } from "@/components/Bits";
import { PERIODS, PHASES, PRESET_INFO, Select } from "@/lib/periods";
import { shareOrCopy } from "@/lib/share";

const STEPS = ["Nom", "Type", "Barème", "Période", "Défi", "Invitations"];

const Choice = ({ active, onClick, title, text, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`w-full rounded-2xl border p-4 text-left transition-colors ${active ? "border-[#B7F52A] bg-[#B7F52A]/10" : "border-white/10 hover:border-white/25"}`}>
    <p className="font-bold text-white">{title}</p>{text && <p className="mt-1 text-xs text-slate-400">{text}</p>}
  </button>
);

export default function CreateLeague() {
  const { user, loadLeagues, setCurrentLeagueId } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ name: "", description: "", emblem: "trophy", mode: "classic", preset: "competition", main_period: { type: "month", phase: "full" }, sandbox: false });
  const [tpls, setTpls] = useState([]);
  const [tpl, setTpl] = useState("resto");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => { api.get("/challenge-templates").then(({ data }) => setTpls(data.templates.filter((t) => t.key !== "libre"))); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const mp = { type: f.main_period.type, phase: f.main_period.phase || "full", ...(f.main_period.type === "custom" ? { start: f.main_period.start, end: f.main_period.end } : {}) };
      const { data: league } = await api.post("/leagues", { ...f, description: f.description || null, main_period: mp });
      const t = tpls.find((x) => x.key === tpl);
      if (t) {
        await api.post(`/leagues/${league.id}/challenges`, { kind: t.condition === "manual" ? "group" : "collective", template_key: t.key, title: t.title, category: t.category,
          all_members: true, participant_ids: [], period: { type: t.period, offset: 0, phase: "full" }, condition: t.condition, consequence: t.consequence, tie_rule: "all_tied" }).catch(() => {});
      }
      const { data: inv } = await api.post(`/leagues/${league.id}/invitations`, { expires_in_hours: 168, max_uses: 19 });
      await loadLeagues();
      setCurrentLeagueId(league.id);
      setCreated({ league, link: inv.link });
      setStep(5);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const next = () => {
    if (step === 0 && f.name.trim().length < 3) return toast.error("Le nom doit contenir au moins 3 caractères.");
    if (step === 3 && f.main_period.type === "custom" && (!f.main_period.start || !f.main_period.end)) return toast.error("Indiquez les dates de la période.");
    if (step === 4) return create();
    setStep(step + 1);
  };

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader eyebrow={`Étape ${step + 1} sur ${STEPS.length} · ${STEPS[step]}`} title="Nouvelle ligue" />
      <div className="mb-6 flex gap-1">{STEPS.map((s, i) => <span key={s} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-[#B7F52A]" : "bg-white/10"}`} />)}</div>
      <div className="space-y-3 mpl-rise" key={step} data-testid={`create-step-${step}`}>
        {step === 0 && (<>
          <input className="mpl-input" placeholder="Nom de la ligue (ex. Les Bureaux du 4e)" maxLength={50} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="create-league-name" autoFocus />
          <textarea className="mpl-input min-h-[80px]" placeholder="Description (facultative)" maxLength={280} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="create-league-description" />
          <p className="mpl-eyebrow pt-2">Emblème</p>
          <div className="flex flex-wrap gap-2">{Object.entries(AVATAR_ICONS).map(([k, Icon]) => (
            <button key={k} type="button" onClick={() => set("emblem", k)} aria-label={k} data-testid={`create-emblem-${k}`}
              className={`flex h-11 w-11 items-center justify-center rounded-xl border ${f.emblem === k ? "border-[#B7F52A] bg-[#B7F52A] text-[#05070D]" : "border-white/10 text-slate-300"}`}><Icon size={20} /></button>
          ))}</div>
          {user.role === "superadmin" && <label className="flex items-center gap-2 pt-2 text-xs text-amber-300"><input type="checkbox" checked={f.sandbox} onChange={(e) => set("sandbox", e.target.checked)} data-testid="create-league-sandbox" /> Ligue de test (matchs fictifs, réservée à l'administration)</label>}
        </>)}
        {step === 1 && (<>
          <Choice active={f.mode === "classic"} onClick={() => set("mode", "classic")} title="Classique" text="Tous les matchs NBA du jour sont proposés automatiquement." testId="create-mode-classic" />
          <Choice active={f.mode === "custom"} onClick={() => set("mode", "custom")} title="Personnalisée" text="Vous pouvez retirer des matchs, ajuster le barème et les pronostics bonus avant le démarrage." testId="create-mode-custom" />
        </>)}
        {step === 2 && Object.entries(PRESET_INFO).map(([k, p]) => (
          <Choice key={k} active={f.preset === k} onClick={() => set("preset", k)} title={`${p.label}${k === "competition" ? " (recommandé)" : ""}`} text={p.text} testId={`create-preset-${k}`} />
        ))}
        {step === 3 && (<>
          <Select value={f.main_period.type} onChange={(v) => set("main_period", { ...f.main_period, type: v })} options={PERIODS} testId="create-period-type" />
          {f.main_period.type === "nba_season" && <Select value={f.main_period.phase} onChange={(v) => set("main_period", { ...f.main_period, phase: v })} options={PHASES} testId="create-period-phase" />}
          {f.main_period.type === "custom" && (<div className="grid grid-cols-2 gap-2">
            <input type="date" className="mpl-input" value={f.main_period.start || ""} onChange={(e) => set("main_period", { ...f.main_period, start: e.target.value })} data-testid="create-period-start" />
            <input type="date" className="mpl-input" value={f.main_period.end || ""} onChange={(e) => set("main_period", { ...f.main_period, end: e.target.value })} data-testid="create-period-end" />
          </div>)}
          <p className="text-xs text-slate-400">À la fin de chaque période, le champion et le dernier entrent au palmarès. Le classement général continue d'une période à l'autre.</p>
        </>)}
        {step === 4 && (<>
          <p className="text-sm text-slate-300">Choisissez un premier défi pour toute la ligue. Chaque membre devra l'accepter.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {tpls.map((t) => <Choice key={t.key} active={tpl === t.key} onClick={() => setTpl(t.key)} title={t.title} text={t.consequence} testId={`create-tpl-${t.key}`} />)}
            <Choice active={!tpl} onClick={() => setTpl(null)} title="Plus tard" text="Je créerai un défi depuis la ligue." testId="create-tpl-none" />
          </div>
        </>)}
        {step === 5 && created && (
          <div className="mpl-card space-y-3 p-5" data-testid="create-done">
            <p className="font-display text-2xl font-extrabold uppercase">« {created.league.name} » est prête !</p>
            <p className="text-sm text-slate-300">Envoyez ce lien à vos amis ou collègues (valable 7 jours, 19 places). Il n'est affiché qu'une fois : vous pourrez en créer d'autres dans l'onglet Invitations.</p>
            <input readOnly value={created.link} className="mpl-input font-mono-num text-xs" data-testid="create-invite-link" />
            <div className="flex flex-wrap gap-2">
              <button onClick={() => { navigator.clipboard.writeText(created.link); toast.success("Lien copié"); }} className="mpl-btn-ghost" data-testid="create-invite-copy"><Copy size={14} /> Copier</button>
              <button onClick={() => shareOrCopy({ title: "Ma Petite Ligue", text: `Rejoins ma ligue « ${created.league.name} » sur Ma Petite Ligue !`, url: created.link })} className="mpl-btn-ghost" data-testid="create-invite-share"><Share2 size={14} /> Partager</button>
              <button onClick={() => nav(`/ligues/${created.league.id}`)} className="mpl-btn-primary" data-testid="create-go-league">Ouvrir la ligue</button>
            </div>
          </div>
        )}
      </div>
      {step < 5 && (
        <div className="mt-6 flex justify-between gap-2">
          <button onClick={() => (step ? setStep(step - 1) : nav(-1))} className="mpl-btn-ghost" data-testid="create-back"><ArrowLeft size={15} /> Retour</button>
          <button onClick={next} disabled={busy} className="mpl-btn-primary" data-testid={step === 4 ? "create-league-submit" : "create-next"}>{step === 4 ? (busy ? "Création…" : "Créer et inviter") : "Continuer"} <ArrowRight size={15} /></button>
        </div>
      )}
    </div>
  );
}
